<script lang="ts">
import type { InviteRowItem } from '@/components/member/MemberInviteList.vue'

export const accessSummary = (row: InviteRowItem, joinsAll = false) => {
  if (joinsAll) return 'Everything inside'
  const count: number = row.join_project_ids?.length || 0
  return count > 0 ? `Can join ${count} more` : 'This project'
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { ChevronRight, FolderTree, UserCog } from 'lucide-vue-next'

import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import RoleSelect from '@/components/roles/RoleSelect.vue'
import { joinsSubProjects } from '@/utils/accessDomains'
import type { ProjectWithLevel } from '@/utils/projectTree'

const props = defineProps<{
  row: InviteRowItem
  currentProjectName: string
  subProjects: ProjectWithLevel[]
}>()

const emit = defineEmits<{
  'update:row': [patch: Partial<InviteRowItem>]
}>()

const store = useStore()
const accessOpen = ref(false)

const selectedIds = computed<number[]>(() => props.row.join_project_ids || [])
const roleJoinsAll = computed(() => joinsSubProjects(store.getters['access/role'](props.row.role)))

const toggleSubProject = (projectId: number, value: boolean) => {
  const next = value
    ? [...selectedIds.value, projectId]
    : selectedIds.value.filter(id => id !== projectId)
  emit('update:row', { join_project_ids: next })
}
</script>

<template>
  <div class="ml-11 mt-1 text-sm">
    <div class="flex items-center gap-3 py-1">
      <UserCog class="w-4 h-4 text-muted-foreground shrink-0" />
      <span class="flex-1 min-w-0">Role</span>
      <RoleSelect
        kind="project"
        :model-value="Number(row.role) || null"
        trigger-class="h-7 w-auto gap-1 px-2 text-xs"
        hide-create
        @update:model-value="v => emit('update:row', { role: v })"
      />
    </div>

    <p v-if="roleJoinsAll" class="flex items-center gap-3 py-1.5 text-muted-foreground" data-testid="role-joins-all-hint">
      <FolderTree class="w-4 h-4 shrink-0" />
      <span>With this role they can join every project inside {{ currentProjectName }}, including ones added later.</span>
    </p>

    <Collapsible v-else-if="subProjects.length > 0" v-model:open="accessOpen">
      <CollapsibleTrigger class="flex w-full items-center gap-3 py-1.5 text-left">
        <FolderTree class="w-4 h-4 text-muted-foreground shrink-0" />
        <span class="flex-1 min-w-0">Can also join</span>
        <span class="text-muted-foreground">{{ selectedIds.length ? `${selectedIds.length} selected` : 'None' }}</span>
        <ChevronRight
          class="w-4 h-4 text-muted-foreground transition-transform"
          :class="{ 'rotate-90': accessOpen }"
        />
      </CollapsibleTrigger>

      <CollapsibleContent class="pl-7 pb-1">
        <p class="pb-1.5 text-xs text-muted-foreground">
          They can join the projects you pick here on their own, whenever they need them.
        </p>
        <div class="max-h-[160px] overflow-y-auto space-y-1.5">
          <label
            v-for="p in subProjects"
            :key="p.project_id"
            class="flex items-center gap-2 cursor-pointer"
            :style="{ paddingLeft: `${p._level * 16}px` }"
          >
            <Checkbox
              :model-value="selectedIds.includes(p.project_id)"
              @update:model-value="(v: boolean | 'indeterminate') => toggleSubProject(p.project_id, v === true)"
            />
            <span class="truncate">{{ p.project_name }}</span>
          </label>
        </div>
      </CollapsibleContent>
    </Collapsible>
  </div>
</template>
